import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import client from '../api/client';
import { toISODate, formatDate } from '../utils/dates';
import { subDays, format } from 'date-fns';

function CheckInSummary({ ci }) {
  const bits = [];
  if (ci.painLevel != null) bits.push(`Pain ${ci.painLevel}/10`);
  if (ci.isFlareDay) bits.push('Flare');
  if (ci.isPeriodDay) bits.push('Period');
  if (ci.meals?.length > 0) bits.push(`${ci.meals.length} meal${ci.meals.length !== 1 ? 's' : ''}`);

  if (bits.length === 0) return null;

  return (
    <div className="flex items-center gap-2 text-sm text-white/50">
      {bits.map((bit, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="text-white/20">·</span>}
          <span>{bit}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

export default function HistoryPage() {
  const [checkIns, setCheckIns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [daysBack, setDaysBack] = useState(30);
  const [confirmingId, setConfirmingId] = useState(null);
  const navigate = useNavigate();

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (confirmingId !== id) {
      setConfirmingId(id);
      return;
    }
    try {
      await client.delete(`/checkins/${id}`);
      setCheckIns(prev => prev.filter(ci => ci.id !== id));
    } catch {
      alert('Failed to delete check-in.');
    }
    setConfirmingId(null);
  };

  useEffect(() => {
    setLoading(true);
    const end = toISODate(new Date());
    const start = toISODate(subDays(new Date(), daysBack));
    client.get('/checkins', { params: { startDate: start, endDate: end } })
      .then(res => setCheckIns(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [daysBack]);

  // Group by checkInDate
  const grouped = checkIns.reduce((acc, ci) => {
    (acc[ci.checkInDate] = acc[ci.checkInDate] || []).push(ci);
    return acc;
  }, {});

  const sortedDates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  if (loading && checkIns.length === 0) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white drop-shadow-sm">Check-in History</h1>
        <p className="text-white/70 mt-1">View and edit past check-ins</p>
      </div>

      {sortedDates.length === 0 ? (
        <div className="glass rounded-2xl p-8 text-center">
          <p className="text-white/50">No check-ins yet. Start by doing your first check-in!</p>
        </div>
      ) : (
        sortedDates.map(date => (
          <div key={date} className="glass rounded-2xl p-4 space-y-2">
            <h2 className="text-sm font-semibold text-white/50 uppercase tracking-wide px-1">
              {formatDate(date)}
            </h2>
            {grouped[date]
              .sort((a, b) => (a.checkInType === 'MORNING' ? -1 : 1))
              .map(ci => {
                const time = ci.createdAt
                  ? format(new Date(ci.createdAt + (ci.createdAt.includes('Z') ? '' : 'Z')), 'h:mm a')
                  : null;

                return (
                  <div
                    key={ci.id}
                    className="bg-white/5 rounded-xl p-4 hover:bg-white/10 transition cursor-pointer"
                    onClick={() => navigate(`/checkin/${ci.id}`)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold uppercase text-white/40 tracking-wide">
                            {ci.checkInType}
                          </span>
                          {time && (
                            <span className="text-xs text-white/30">{time}</span>
                          )}
                        </div>
                        <CheckInSummary ci={ci} />
                      </div>

                      <div className="flex items-center gap-3 flex-shrink-0 pt-1">
                        {confirmingId === ci.id ? (
                          <>
                            <button
                              onClick={(e) => { e.stopPropagation(); setConfirmingId(null); }}
                              className="text-white/30 hover:text-white transition text-sm"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={(e) => handleDelete(e, ci.id)}
                              className="text-red-400 hover:text-red-300 transition text-sm font-medium"
                            >
                              Confirm?
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={(e) => handleDelete(e, ci.id)}
                              className="text-white/30 hover:text-red-400 transition text-sm"
                            >
                              Delete
                            </button>
                            <span className="text-white/30 text-sm">Edit &rarr;</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        ))
      )}

      <button
        onClick={() => setDaysBack(d => d + 30)}
        disabled={loading}
        className="w-full py-3 bg-white/10 text-white/60 rounded-xl hover:bg-white/15 transition disabled:opacity-50"
      >
        {loading ? 'Loading...' : 'Load older check-ins'}
      </button>
    </div>
  );
}
